/**
 * Check if we're on a preview frontend.
 *
 * Every edge endpoint is picked from this hostname check alone, never from
 * REACT_APP_EDGE_* build variables. Cloudflare Pages builds preview
 * deployments with the preview environment's variables, so a production
 * test domain served from a preview deployment (e.g. early-fe.dcams.app)
 * sent its status polls and queue submissions to preview.dcams.app while
 * its session lived on production.
 */
export const isPreviewFrontend = () => {
  const hostname = window.location.hostname;
  return hostname === "preview-fe.dcams.app" ||
    hostname.includes("fe-dcams.pages.dev") ||
    hostname.includes("ngrok") ||
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname === "10.0.2.2";
};

/**
 * Get the edge API base URL based on the current hostname.
 * Uses preview.dcams.app for preview-fe.dcams.app and pages.dev deployments.
 * Uses dcams.app for production.
 */
export const getEdgeBaseUrl = () => {
  return isPreviewFrontend() ? "https://preview.dcams.app" : "https://dcams.app";
};

/**
 * Get the edge start_session URL (POST - sends device info, initializes session)
 */
export const getEdgeStartSessionUrl = () => {
  if (isPreviewFrontend()) {
    return "https://preview.dcams.app/edge/frontend/api/start_session";
  }
  return "https://dcams.app/edge/frontend/api/start_session";
};

/**
 * Poll cadence after a not-ready answer from get_session. The first call
 * is never delayed: the POST probe returns immediately (use_legacy or
 * not-ready) without a DB probe loop, so waiting up front would only add
 * latency to the legacy path.
 */
export const EDGE_POLL_RETRY_MS = 2000;

/**
 * Body for the first get_session call. This POST replaces the old
 * start_session round trip: the worker records device_info + url_params,
 * flips process_state, then runs the normal get_session path.
 *
 * fingerprint_pending tells the worker Fingerprint has not run yet. For
 * sites with device fraud rules it answers { ready:false,
 * fingerprint_required:true } immediately instead of waiting for a
 * fingerprint webhook that cannot exist yet.
 */
export const buildEdgeFirstCallPayload = ({ requestId, deviceInfo, fingerprintPending, visitorId }) => {
  const payload = {
    request_id: requestId,
    device_info: deviceInfo,
    fingerprint_pending: fingerprintPending === true,
  };
  if (visitorId) payload.visitor_id = visitorId;

  const currentUrlParams = new URLSearchParams(window.location.search);
  const urlParamsObject = {};
  for (const [key, value] of currentUrlParams.entries()) {
    urlParamsObject[key] = value;
  }
  if (Object.keys(urlParamsObject).length > 0) {
    payload.url_params = urlParamsObject;
  }
  return payload;
};

/**
 * Get the edge get_session URL.
 *  - POST (no path param, JSON body from buildEdgeFirstCallPayload): first call
 *  - GET /{request_id}?visitor_id=: every later poll
 */
export const getEdgeGetSessionUrl = () => {
  if (isPreviewFrontend()) {
    return "https://preview.dcams.app/edge/frontend/api/get_session";
  }
  return "https://dcams.app/edge/frontend/api/get_session";
};

/**
 * Presigned S3 upload URL endpoint (dcams-journey-images worker).
 * Preview frontends talk to the preview worker; production to vx.dcams.app.
 */
export const getEdgeUploadUrl = () => {
  if (isPreviewFrontend()) {
    return "https://preview.dcams.app/v1/upload-url";
  }
  return "https://vx.dcams.app/v1/upload-url";
};

/**
 * Get the edge queue URL
 * Hostname only: preview hosts -> preview.dcams.app, everything else -> production
 */
export const getEdgeQueueUrl = () => {
  if (isPreviewFrontend()) {
    return "https://preview.dcams.app/edge/frontend/api/queue/batch";
  }
  return "https://dcams.app/edge/frontend/api/queue/batch";
};

/**
 * Get the edge status URL
 * Hostname only: preview hosts -> preview.dcams.app, everything else -> production
 */
export const getEdgeStatusUrl = () => {
  if (isPreviewFrontend()) {
    return "https://preview.dcams.app/edge/frontend/api/status";
  }
  return "https://dcams.app/edge/frontend/api/status";
};

/**
 * Get the edge retrieve URL (for IDMax)
 * Hostname only: preview hosts -> preview.dcams.app, everything else -> production
 */
export const getEdgeRetrieveUrl = () => {
  if (isPreviewFrontend()) {
    return "https://preview.dcams.app/edge/backend/api/idmax/retrieve";
  }
  return "https://dcams.app/edge/backend/api/idmax/retrieve";
};

/**
 * Get the device signals URL
 * Uses preview.dcams.app for preview/localhost, vx.dcams.app for production
 */
/**
 * Get the capture challenge URL
 * Uses preview.dcams.app for preview/localhost, dcams.app for production
 */
export const getEdgeChallengeUrl = () => {
  if (isPreviewFrontend()) {
    return "https://preview.dcams.app/edge/frontend/api/capture/challenge";
  }
  return "https://dcams.app/edge/frontend/api/capture/challenge";
};

/**
 * Get the capture envelope signing URL
 * Uses preview.dcams.app for preview/localhost, dcams.app for production
 */
export const getEdgeSignUrl = () => {
  if (isPreviewFrontend()) {
    return "https://preview.dcams.app/edge/frontend/api/capture/sign";
  }
  return "https://dcams.app/edge/frontend/api/capture/sign";
};

/**
 * Get the capture telemetry URL (fire-and-forget funnel milestones)
 * Uses preview.dcams.app for preview/localhost, dcams.app for production
 */
export const getEdgeTelemetryUrl = () => {
  return `${getEdgeBaseUrl()}/edge/frontend/api/queue/telemetry`;
};

/**
 * Get the device signals URL
 * Uses preview.dcams.app for preview/localhost, vx.dcams.app for production
 */
export const getDeviceSignalsUrl = () => {
  if (isPreviewFrontend()) {
    return "https://preview.dcams.app/frontend/session/device-signals";
  }
  return "https://vx.dcams.app/frontend/session/device-signals";
};
